import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from './common/Tooltip';
import DonutChart from './DonutChart';
import { parseFloatFromString, formatCurrency, formatPercentage, getChangeColorClass, formatChangedCurrencyAmount, formatChangedPercentage } from '../utils/formatters';
import { positionsListNavState } from '../utils/positionsListNavState';
import { usePositionContext } from '../contexts/PositionContext';

const formatSegmentTitle = (label, positions, pnl, roi) =>
  `${label} (${positions}) - PnL ${formatChangedCurrencyAmount(pnl)} (${formatChangedPercentage(roi)})`;

const COLORS = {
  large: '#3B82F6', // blue-500
  mid: '#22C55E',   // green-500
  low: '#EAB308',   // yellow-500
};

const VIEW_OPTIONS = [
  { id: 'current', label: 'Current' },
  { id: 'invested', label: 'Invested' },
  { id: 'plan', label: 'Plan' },
];

const MarketCapBreakdown = ({ marketCapCategories = [], goal = null, onOpenGoals }) => {
  const [view, setView] = useState('current');
  const navigate = useNavigate();
  const { positions = [] } = usePositionContext();

  // Load saved view preference
  useEffect(() => {
    const saved = localStorage.getItem('dashboard_mcap_view');
    if (saved && VIEW_OPTIONS.some((v) => v.id === saved)) {
      setView(saved);
    }
  }, []);

  // Save view preference
  const handleViewChange = (newView) => {
    setView(newView);
    localStorage.setItem('dashboard_mcap_view', newView);
  };

  // Aggregate into 3 buckets
  const buckets = useMemo(() => {
    const result = {
      large: { invested: 0, holding: 0, pnl: 0, positions: 0 },
      mid: { invested: 0, holding: 0, pnl: 0, positions: 0 },
      low: { invested: 0, holding: 0, pnl: 0, positions: 0 },
    };

    (marketCapCategories || []).forEach((c) => {
      const name = c?.name || '';
      const value = parseFloatFromString(c?.value);
      const totalCost = parseFloatFromString(c?.costs); // costs is now totalCost
      const proceeds = parseFloatFromString(c?.proceeds);
      const invested = totalCost || 0; // total originally invested
      const pnl = (value || 0) + (proceeds || 0) - (totalCost || 0); // current PnL

      let key = 'low';
      if (name === 'Large Cap') key = 'large';
      else if (name === 'Mid Cap') key = 'mid';

      result[key].invested += invested;
      result[key].holding += value || 0;
      result[key].pnl += pnl || 0;
    });

    // Count positions per bucket
    positions.forEach((p) => {
      const amt = parseFloatFromString(p?.holdingsAmount);
      if (!amt || amt <= 0) return;
      const cat = p?.token?.marketData?.marketCapCategory;
      if (cat === 'Large Cap') result.large.positions += 1;
      else if (cat === 'Mid Cap') result.mid.positions += 1;
      else result.low.positions += 1;
    });

    return result;
  }, [marketCapCategories, positions]);

  // Calculate totals
  const totals = useMemo(() => ({
    invested: buckets.large.invested + buckets.mid.invested + buckets.low.invested,
    holding: buckets.large.holding + buckets.mid.holding + buckets.low.holding,
    plan: 100, // Plan is always 100%
  }), [buckets]);

  // Target allocation from goal
  const targets = useMemo(() => {
    if (!goal) return null;
    return {
      large: Number(goal.distLarge) || 0,
      mid: Number(goal.distMid) || 0,
      low: Number(goal.distLow) || 0,
    };
  }, [goal]);

  // Calculate percentages
  const percentages = useMemo(() => {
    const investedTotal = totals.invested || 1;
    const holdingTotal = totals.holding || 1;

    return {
      large: {
        invested: (buckets.large.invested / investedTotal) * 100,
        current: (buckets.large.holding / holdingTotal) * 100,
        plan: targets?.large ?? 0,
      },
      mid: {
        invested: (buckets.mid.invested / investedTotal) * 100,
        current: (buckets.mid.holding / holdingTotal) * 100,
        plan: targets?.mid ?? 0,
      },
      low: {
        invested: (buckets.low.invested / investedTotal) * 100,
        current: (buckets.low.holding / holdingTotal) * 100,
        plan: targets?.low ?? 0,
      },
    };
  }, [buckets, totals, targets]);

  // Prepare chart data based on view - always show all 3 categories (with tooltip and key for segment click)
  const chartData = useMemo(() => {
    const getValue = (key) => {
      if (view === 'invested') return buckets[key].invested;
      if (view === 'plan') return targets?.[key] ?? 0;
      return buckets[key].holding; // current
    };

    return ['large', 'mid', 'low'].map((key) => {
      const b = buckets[key];
      const roi = b.invested > 0 ? ((b.holding - b.invested) / b.invested) * 100 : 0;
      const label = key === 'large' ? 'Large Cap' : key === 'mid' ? 'Mid Cap' : 'Low Cap';
      const tooltip = formatSegmentTitle(label, b.positions, b.pnl, roi);
      return {
        key,
        name: label,
        value: getValue(key),
        color: COLORS[key],
        tooltip,
      };
    });
  }, [view, buckets, targets]);

  // Table rows with all data
  const tableRows = [
    { key: 'large', label: 'Large Cap', color: COLORS.large },
    { key: 'mid', label: 'Mid Cap', color: COLORS.mid },
    { key: 'low', label: 'Low Cap', color: COLORS.low },
  ].map((r) => {
    const deviation = targets ? percentages[r.key].current - percentages[r.key].plan : null;
    const investedAmt = buckets[r.key].invested;
    const holdingAmt = buckets[r.key].holding;
    // Calculate ROI from holdings vs invested: (current - invested) / invested * 100
    const roi = investedAmt > 0 ? ((holdingAmt - investedAmt) / investedAmt) * 100 : 0;
    return {
      ...r,
      positions: buckets[r.key].positions,
      investedPct: percentages[r.key].invested,
      investedAmt,
      currentPct: percentages[r.key].current,
      holdingAmt,
      roi,
      plan: percentages[r.key].plan,
      deviation,
      pnl: buckets[r.key].pnl,
    };
  });

  const getDeviationClass = (deviation) => {
    if (deviation === null) return '';
    const abs = Math.abs(deviation);
    if (abs > 10) return 'text-red-600 dark:text-red-400';
    if (abs > 5) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const hasPlan = targets !== null;

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
          Market Cap
        </h3>
        <div className="flex gap-1">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => handleViewChange(opt.id)}
              disabled={opt.id === 'plan' && !hasPlan}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${
                view === opt.id
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              } ${opt.id === 'plan' && !hasPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={onOpenGoals}
          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          {hasPlan ? 'Edit Goals' : 'Set Goals'}
        </button>
      </div>

      {/* Donut Chart + Combined Legend/Table */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Donut Chart */}
        <div className="relative flex-shrink-0 flex items-center justify-center mx-auto sm:mx-0" style={{ width: 140, height: 140 }}>
          <DonutChart
            data={chartData}
            animationKey={view}
            size={140}
            thickness={26}
            totalLabel=""
            showLabels={false}
            valueFormatter={(v) => `${v.toFixed(0)}%`}
            showLegend={false}
            onSegmentClick={(segment) => navigate('/positions', { state: positionsListNavState({ mc: segment.key }) })}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            {view === 'plan' && goal?.targetMultiple ? (
              <>
                <span className="text-xs text-gray-500 dark:text-gray-400">Goal</span>
                <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {parseFloat(goal.targetMultiple).toFixed(1)}x
                </span>
              </>
            ) : (
              <>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {view === 'invested' ? 'Invested' : 'Holdings'}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(view === 'invested' ? totals.invested : totals.holding)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Combined Legend/Table */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 dark:text-gray-500 text-[10px]">
                <th className="text-left font-normal pb-1"></th>
                {view === 'invested' && (
                  <>
                    <th className="text-right font-normal pb-1">Invested %</th>
                    <th className="text-right font-normal pb-1">Invested</th>
                    <th className="text-right font-normal pb-1">PnL</th>
                  </>
                )}
                {view === 'current' && (
                  <>
                    <th className="text-right font-normal pb-1">Holdings %</th>
                    <th className="text-right font-normal pb-1">Holdings</th>
                    <th className="text-right font-normal pb-1">ROI</th>
                  </>
                )}
                {view === 'plan' && (
                  <>
                    <th className="text-right font-normal pb-1">Plan</th>
                    <th className="text-right font-normal pb-1">Current</th>
                    <th className="text-right font-normal pb-1">Deviation</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, idx) => (
                <tr
                  key={r.key}
                  className={idx > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}
                >
                  <td className="py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: r.color }} />
                      <Tooltip title={formatSegmentTitle(r.label, r.positions, r.pnl, r.roi)} position="top" arrow={true} theme="dark" size="small">
                        <button
                          onClick={() => navigate('/positions', { state: positionsListNavState({ mc: r.key }) })}
                          className="text-gray-800 dark:text-gray-200 hover:underline text-left"
                        >
                          {r.label} ({r.positions})
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                  {view === 'invested' && (
                    <>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                        {formatPercentage(r.investedPct)}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatCurrency(r.investedAmt)}
                      </td>
                      <td className={`py-1.5 text-right font-medium tabular-nums ${getChangeColorClass(r.pnl)}`}>
                        {formatCurrency(r.pnl)}
                      </td>
                    </>
                  )}
                  {view === 'current' && (
                    <>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                        {formatPercentage(r.currentPct)}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatCurrency(r.holdingAmt)}
                      </td>
                      <td className={`py-1.5 text-right font-medium tabular-nums ${getChangeColorClass(r.roi)}`}>
                        {r.roi >= 0 ? '+' : ''}{formatPercentage(r.roi)}
                      </td>
                    </>
                  )}
                  {view === 'plan' && (
                    <>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 tabular-nums">
                        {formatPercentage(r.plan)}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400 tabular-nums">
                        {formatPercentage(r.currentPct)}
                      </td>
                      <td className={`py-1.5 text-right font-medium tabular-nums ${getDeviationClass(r.deviation)}`}>
                        {r.deviation !== null ? (r.deviation >= 0 ? '+' : '') + formatPercentage(r.deviation) : '—'}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MarketCapBreakdown;
